import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogType?: string;
  schema?: object;
}

export function SEO({ title, description, keywords, canonical, ogType = 'website', schema }: SEOProps) {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const fullUrl = canonical || `${siteUrl}${location.pathname}`;
  const currentDate = new Date().toISOString().split('T')[0];

  useEffect(() => {
    // Title
    document.title = title;

    // Meta tags
    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: currentDate },
      
      // Open Graph
      { property: 'og:type', content: ogType },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: fullUrl },
      { property: 'og:site_name', content: '株式会社ツーサム' },
      
      // Twitter Card
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ];

    metaTags.forEach(({ name, property, content }) => {
      const attribute = name ? 'name' : 'property';
      const value = name || property;
      let element = document.querySelector(`meta[${attribute}="${value}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, value!);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    });

    // Canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullUrl);

    // Schema.org JSON-LD
    if (schema) {
      let schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    }
  }, [title, description, keywords, fullUrl, ogType, schema, currentDate]);

  return null;
}

// Schema.org generators
export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '株式会社ツーサム',
    url: siteUrl,
    logo: 'https://static.readdy.ai/image/a173b05d2bea81f49058e98548b205ed/610095eea486ea38414ef21e2281d099.png',
    description: '株式会社ツーサムは東京都北区赤羽に拠点を置く総合ビジネスソリューション企業です。プリントサービス、ビジネスコンサルティング、デジタルマーケティング、プロモーション事業を通じて、中小企業のデジタル変革と成長を支援します。',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '赤羽2-8-1 大洋堂ビル2F',
      addressLocality: '北区',
      addressRegion: '東京都',
      postalCode: '115-0045',
      addressCountry: 'JP'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 35.7773,
      longitude: 139.7206
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+81-3-5249-3995',
      contactType: 'customer service',
      areaServed: 'JP',
      availableLanguage: 'Japanese'
    },
    founder: {
      '@type': 'Person',
      name: '中林恵'
    },
    foundingDate: '2000-06',
    email: 'info@twosum.co.jp'
  };
};

export const generateWebPageSchema = (name: string, description: string, url: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url,
    publisher: {
      '@type': 'Organization',
      name: '株式会社ツーサム',
      url: siteUrl
    },
    inLanguage: 'ja-JP'
  };
};

export const generateLocalBusinessSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: '株式会社ツーサム',
    image: 'https://static.readdy.ai/image/a173b05d2bea81f49058e98548b205ed/610095eea486ea38414ef21e2281d099.png',
    '@id': siteUrl,
    url: siteUrl,
    telephone: '+81-3-5249-3995',
    priceRange: '¥¥',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '赤羽2-8-1 大洋堂ビル2F',
      addressLocality: '北区',
      addressRegion: '東京都',
      postalCode: '115-0045',
      addressCountry: 'JP'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 35.7773,
      longitude: 139.7206
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday'
      ],
      opens: '09:00',
      closes: '18:00'
    }
  };
};

export const generateServiceSchema = (services: Array<{ name: string; description: string; price?: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: services.map((service, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Service',
        name: service.name,
        description: service.description,
        provider: {
          '@type': 'Organization',
          name: '株式会社ツーサム',
          url: siteUrl
        },
        ...(service.price && {
          offers: {
            '@type': 'Offer',
            price: service.price,
            priceCurrency: 'JPY'
          }
        })
      }
    }))
  };
};

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
};
